import _ from 'lodash';
import url from 'url';
import queryString from 'query-string';
import { useEffect, useRef } from 'react';
import {
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInMonths,
  differenceInCalendarMonths,
  differenceInWeeks,
  differenceInYears,
  format,
  isDate,
  parseISO,
} from 'date-fns';
import pluralize from 'pluralize';

import { transformToDate } from '@nerdwallet/utilities';
import { NWDate } from '@nerdwallet/utilities/dist/date-utils/common/types';
import {
  AP_LONG_MONTH_DATE_FORMAT,
  AP_SHORT_MONTH_DATE_FORMAT,
} from '@constants/dates';

/**
 * Takes the difference between a given date and the current date and returns an abbreviated string
 * prettily displaying the delta.
 *
 * @returns {string} - A string prettily representing the difference in time (e.g. "3d ago")
 */
export const getShortDateDifference = (
  /* Date to compare versus the current time */
  date: Date | string,
  /* A string you would like to be put at the end */
  suffix = '',
  /* Date from which to calculate difference */
  currentDate?: Date,
): string => {
  const parsedDate = transformToDate(date);
  const today = transformToDate(currentDate);

  const minutes = Math.abs(differenceInMinutes(today, parsedDate));
  const hours = Math.abs(differenceInHours(today, parsedDate));
  const days = Math.abs(differenceInDays(today, parsedDate));
  const years = Math.abs(differenceInYears(today, parsedDate));

  if (!minutes) {
    return 'just now';
  }

  if (minutes < 60) {
    return `${minutes}m${suffix}`;
  }

  if (hours < 24) {
    return `${hours}h${suffix}`;
  }

  if (days <= 365) return `${days}d${suffix}`;

  return `${years}y${suffix}`;
};

/**
 * Returns the amount of time between two dates, for a given unit (years, months, etc).
 *
 * If date input is a string, it must be ISO 8601 format.
 */
export const getDifferenceBetweenDates = (
  /* The later date */
  dateLeft: string | Date,
  /* The earlier date */
  dateRight: string | Date,
  unit: 'years' | 'months' | 'calendarMonths' | 'days',
): number => {
  const startDate = isDate(dateLeft) ? dateLeft : parseISO(dateLeft);
  const endDate = isDate(dateRight) ? dateRight : parseISO(dateRight);

  if (unit === 'years') {
    return differenceInYears(startDate, endDate);
  }

  if (unit === 'months') {
    return differenceInMonths(startDate, endDate);
  }

  if (unit === 'calendarMonths') {
    return differenceInCalendarMonths(startDate, endDate);
  }

  if (unit === 'days') {
    return differenceInDays(startDate, endDate);
  }

  throw new Error('Format not supported');
};

/**
 * Returns the amount of time between two dates
 */
export const getDateDifference = (date: NWDate, currentDate = new Date()) => {
  const parsedDate = transformToDate(date);
  const today = transformToDate(currentDate);

  const minutes = Math.abs(differenceInMinutes(today, parsedDate));
  const hours = Math.abs(differenceInHours(today, parsedDate));
  const days = Math.abs(differenceInDays(today, parsedDate));
  const weeks = Math.abs(differenceInWeeks(today, parsedDate));
  const months = Math.abs(differenceInMonths(today, parsedDate));

  if (minutes < 60) {
    return 'less than an hour';
  }

  if (hours < 24) {
    return pluralize('hours', hours, true);
  }

  if (days < 7) {
    return pluralize('days', days, true);
  }

  if (weeks < 4) {
    return pluralize('weeks', weeks, true);
  }

  return pluralize('months', months, true);
};

/**
 * @param {String} originalUrl - the URL to which the params should be added
 * @param {Object} params - the params to be added.
 *   - Nullish (null/undefined values) will be ignored
 *   - Values will override any existing params in originalUrl
 * @returns {String} A new URL string including the new params
 */
export const addParamsToUrl = (originalUrl, params) => {
  // Parse the URL.  The second argument indicates that we want the `query` field represented as an
  // object.
  const parsedUrl = url.parse(originalUrl, true);
  parsedUrl.search = queryString.stringify({
    ...parsedUrl.query,
    ..._.omitBy(params, _.isNil), // Disregard nullish param values
  });
  return url.format(parsedUrl);
};

/**
 * Removes a specified query parameter from the current URL and updates the browser's address bar.
 *
 * This function modifies the URL of the current page by removing the query parameter provided
 * and then updates the browser's history state to reflect the new URL without reloading the page.
 *
 * @param {string} queryParam - The name of the query parameter to remove from the URL.
 *
 * @example
 * // Assuming the current URL is 'https://example.com?page=1&sort=asc'
 * removeQueryParam('sort');
 * // The URL will be updated to 'https://example.com?page=1'
 */
export const removeQueryParam = (queryParam: string) => {
  const currentUrl = new URL(window.location.href);
  const currentParams = new URLSearchParams(currentUrl.search);

  currentParams.delete(queryParam);
  currentUrl.search = currentParams.toString();

  window.history.replaceState({}, '', `${currentUrl.toString()}`);
};

/**
 * Formats a date in the Associated Press style.
 *
 * If the month is March, April, May, June, or July, then do not abbreviate the month.
 * Otherwise, abbreivate the month like "Jan.". These months are always spelled
 * out (as dictated by AP style rules), and also because users might confuse
 * them for typos.
 *
 * Examples:
 * - "The deadline to file your taxes is April 15, 2024."
 * - "The FAFSA form must be submitted by 11:59pm on June 30, 2022."
 */
export const formatApStyleDate = (date: Date) => {
  if (!date) return '';

  // If month is March, April, May, June, or July
  if ([2, 3, 4, 5, 6].includes(date.getMonth())) {
    return format(date, AP_LONG_MONTH_DATE_FORMAT);
  }

  return format(date, AP_SHORT_MONTH_DATE_FORMAT);
};

/**
 * Custom React Hook to get previous value from props or state.
 *
 * Reference:
 * {@link https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state}
 *
 * @param {*} value - The value to persist for the next render
 *
 * @return {*|null} - The previous value
 */
export const usePrevious = (value) => {
  const ref = useRef(null);
  useEffect(() => {
    ref.current = value;
  });

  return ref.current;
};

/**
 * Throttles a function, limiting its execution frequency based on delay param.
 *
 * @param {Function} func - The function to throttle.
 * @param {number} [delay=300] - The delay in milliseconds between executions.
 * @returns {Function} The throttled function.
 */

export const throttle = (
  func: (...args: any[]) => any,
  delay = 300,
): ((...args: any[]) => any) => {
  let timeoutId: NodeJS.Timeout | null = null;
  let lastCallTime = 0;

  return (...args: any[]) => {
    const now = Date.now();
    const timeSinceLastCall = now - lastCallTime;

    if (timeSinceLastCall >= delay) {
      lastCallTime = now;
      clearTimeout(timeoutId);
      return func(...args);
    }

    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      lastCallTime = Date.now();
      return func(...args);
    }, delay - timeSinceLastCall);
  };
};

/**
 * Checks if an object is empty.
 *
 * @param {object | undefined | null} obj - The object to check.
 * @returns {boolean} True if the object is empty, false otherwise.
 */

export const isEmpty = (obj: object | undefined | null) => {
  // Check if the object is undefined or null
  if (obj === undefined || obj === null) {
    return true;
  }

  // Check if the object is an object
  if (typeof obj === 'object') {
    // Check if the object is empty (no properties)
    return Object.keys(obj).length === 0;
  }

  // If the object is not an object, it is not empty
  return false;
};

export const formatRewards = (number: number) =>
  number === 0 ? number : (number / 100).toFixed(2);

export const stringifyToYearAndMonths = (
  numberInYears: number,
  useAbbreviations = false,
): string => {
  const years = Math.floor(numberInYears);
  const months = Math.ceil(
    _.toNumber(((numberInYears % 1) * 12).toPrecision(7)),
  );

  const yearString =
    years > 0 ? pluralize(useAbbreviations ? 'yr' : 'year', years, true) : '';
  const monthString =
    months > 0 || !years
      ? pluralize(useAbbreviations ? 'mo' : 'month', months, true)
      : '';

  let divider = '';
  if (yearString && monthString) {
    divider = useAbbreviations ? ' ' : ', ';
  }

  return `${yearString}${divider}${monthString}`;
};

export const formatNumber = (number: number) =>
  new Intl.NumberFormat('en-US').format(number);

export const getMobileOperatingSystem = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return null;
  }

  const userAgent = navigator?.userAgent || navigator?.vendor;

  if (/android/i.test(userAgent)) {
    return 'Android';
  }

  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return 'iOS';
  }

  return null;
};
